import { Link } from 'react-router-dom';

export interface BrandLogoProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  to?: string;
  className?: string;
  variant?: 'horizontal' | 'stacked' | 'icon-only';
  theme?: 'dark' | 'light'; // 'dark' = white logo on dark surfaces; 'light' = dark logo on white surfaces
}

export function BrandLogo({
  size = 'md',
  to = '/dashboard',
  className = '',
  variant = 'horizontal',
  theme = 'light',
}: BrandLogoProps) {
  const sizeConfig = {
    xs: {
      horizontalHeight: 'h-6 sm:h-7',
      stackedHeight: 'h-12',
      iconHeight: 'h-6',
    },
    sm: {
      horizontalHeight: 'h-8 sm:h-9',
      stackedHeight: 'h-16 sm:h-20',
      iconHeight: 'h-8',
    },
    md: {
      horizontalHeight: 'h-9 sm:h-10',
      stackedHeight: 'h-24 sm:h-28',
      iconHeight: 'h-9 sm:h-10',
    },
    lg: {
      horizontalHeight: 'h-11 sm:h-12',
      stackedHeight: 'h-28 sm:h-32',
      iconHeight: 'h-12 sm:h-14',
    },
    xl: {
      horizontalHeight: 'h-14 sm:h-16',
      stackedHeight: 'h-36 sm:h-40',
      iconHeight: 'h-16 sm:h-18',
    },
  }[size];

  const getLogoSrc = () => {
    const isDark = theme === 'dark';
    switch (variant) {
      case 'icon-only':
        return isDark ? '/cb-cloud-white.png' : '/cb-cloud-dark.png';
      case 'stacked':
        return isDark ? '/cb-logo-white-stacked.png' : '/cb-logo-dark-stacked.png';
      case 'horizontal':
      default:
        return isDark ? '/cb-logo-white-horizontal.png' : '/cb-logo-dark-horizontal.png';
    }
  };

  const getHeightClass = () => {
    switch (variant) {
      case 'icon-only':
        return sizeConfig.iconHeight;
      case 'stacked':
        return sizeConfig.stackedHeight;
      case 'horizontal':
      default:
        return sizeConfig.horizontalHeight;
    }
  };

  const content = (
    <div className={`inline-flex items-center justify-center select-none group transition-transform duration-200 hover:opacity-95 ${className}`}>
      <img
        src={getLogoSrc()}
        alt="Cloud Binary - Learn by Doing"
        className={`${getHeightClass()} w-auto max-w-full object-contain`}
        loading="eager"
      />
    </div>
  );

  if (to) {
    return (
      <Link to={to} className="inline-flex items-center flex-shrink-0 focus:outline-none">
        {content}
      </Link>
    );
  }

  return content;
}
